import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toIntlDateTimeFormat } from '@zedoc/date';
import { getRecordColor } from '../../../screens/EpisodesOfCare/helpers';
import useForm from '../../../utils/useForm';
import EpisodeOfCareDetails from './EpisodeOfCareDetails';
import { apiZedocGetProjectTracks } from '../../../common/api/zedoc';
import { callMethod } from '../../../common/utilsClient/ddp/actions';

const EnrolmentDetails = ({
  enrolment,
  visible,
  onCancel,
  isEditable,
  onSave,
  onDelete,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tracksOptions, setTracksOptions] = useState([]);
  const [loadingTracks, setLoadingTracks] = useState(false);

  const { context = {} } = enrolment || {};
  const reviewQueueMetadata = enrolment?.reviewQueueMetadata;

  // Get existing phone/email from arrays (source of truth from Recipients)
  const existingPhone = context?.phones?.[0]?.number || '';
  const existingEmail = context?.emails?.[0]?.address || '';

  const { form, isValid, handleSubmit, renderField, setValue } = useForm({
    inputs: {
      patientName: {
        testId: 'form-field-patient-name',
        label: t('forms:recipientName.label'),
        type: 'text',
        defaultValue: context?.patientName,
        showValueOnly: true,
        muted: true,
        disabled: true,
        schema: {
          type: String,
          optional: true,
        },
      },
      patientId: {
        testId: 'form-field-patient-id',
        label: t('forms:recipientId.label'),
        type: 'text',
        defaultValue: enrolment?.patientId,
        showValueOnly: true,
        muted: true,
        disabled: true,
        schema: {
          type: String,
          optional: true,
        },
      },
      projectName: {
        testId: 'form-field-project-name',
        label: t('forms:projectName.label', { defaultValue: 'Project Name' }),
        type: 'text',
        defaultValue: context?.projectLabel,
        showValueOnly: true,
        muted: true,
        disabled: true,
        schema: {
          type: String,
          optional: true,
        },
      },
      phoneNumber: {
        testId: 'form-field-phone-number',
        label: t('forms:phone.label'),
        type: 'phone',
        defaultValue: existingPhone,
        showValueOnly: !isEditable,
        disabled: isSubmitting,
        placeholder: t('forms:phone.placeholder', {
          defaultValue: 'Enter phone number...',
        }),
        schema: {
          type: String,
          optional: true,
        },
      },
      email: {
        testId: 'form-field-email',
        label: t('forms:email.label'),
        type: 'text',
        defaultValue: existingEmail,
        showValueOnly: !isEditable,
        disabled: isSubmitting,
        placeholder: t('forms:email.placeholder', {
          defaultValue: 'Enter email address...',
        }),
        schema: {
          type: String,
          optional: true,
        },
      },
      trackId: {
        testId: 'form-field-track-id',
        label: t('tracks', { count: 1 }),
        type: 'react-select',
        options: tracksOptions,
        defaultValue: context?.trackId,
        showValueOnly: !isEditable,
        disabled: isSubmitting || loadingTracks,
        placeholder: loadingTracks
          ? t('loading', { defaultValue: 'Loading...' })
          : t('selectTrack', { defaultValue: 'Select track...' }),
        schema: {
          type: String,
          optional: true,
        },
      },
    },
    onSubmit: async (values) => {
      if (onSave) {
        setIsSubmitting(true);
        try {
          await onSave(values, false);
          onCancel();
        } catch (error) {
          console.error('Error saving enrolment:', error);
        } finally {
          setIsSubmitting(false);
        }
      } else {
        onCancel();
      }
    },
  });

  useEffect(() => {
    const projectId = context?.projectId;
    if (projectId && visible) {
      setLoadingTracks(true);
      dispatch(callMethod(apiZedocGetProjectTracks, { projectId }))
        .then((tracks) => {
          console.log('EnrolmentDetails: tracks received', tracks);
          setTracksOptions(tracks || []);
        })
        .catch((error) => {
          console.error('Error fetching tracks:', error);
          setTracksOptions([]);
        })
        .finally(() => {
          setLoadingTracks(false);
        });
    }
  }, [context?.projectId, visible, dispatch]);

  useEffect(() => {
    if (
      tracksOptions.length > 0 &&
      context?.trackId &&
      tracksOptions.some((t) => t.value === context.trackId)
    ) {
      setValue('trackId', context.trackId);
    }
  }, [tracksOptions, context?.trackId, setValue]);

  const handleSaveAndProcess = async () => {
    if (onSave) {
      setIsSubmitting(true);
      try {
        await onSave(form, true);
        onCancel();
      } catch (error) {
        console.error('Error processing enrolment:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      onCancel();
    }
  };

  const handleDelete = async () => {
    if (onDelete) {
      setIsSubmitting(true);
      try {
        await onDelete();
        onCancel();
      } catch (error) {
        console.error('Error deleting enrolment:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <EpisodeOfCareDetails
      title={t('enrolmentDetails', { defaultValue: 'Enrolment Details' })}
      visible={visible}
      onCancel={onCancel}
      onSave={handleSubmit}
      onSaveAndProcess={handleSaveAndProcess}
      onDelete={handleDelete}
      isValid={isValid}
      isEditable={isEditable}
    >
      <div className="space-y-4">
        {reviewQueueMetadata && (
          <div
            className={`p-4 rounded-md ${
              reviewQueueMetadata.severity === 'critical'
                ? 'bg-red-100 dark:bg-red-900'
                : reviewQueueMetadata.severity === 'high'
                ? 'bg-yellow-200 dark:bg-yellow-800'
                : 'bg-gray-100 dark:bg-gray-800'
            }`}
          >
            <div className="font-semibold mb-2">
              {reviewQueueMetadata.severity?.toUpperCase()} -{' '}
              {reviewQueueMetadata.type}
            </div>
            <div className="text-sm">
              {reviewQueueMetadata.reasons?.map((reasonObj, index) => (
                <div key={index}>
                  •{' '}
                  {typeof reasonObj === 'string'
                    ? reasonObj
                    : reasonObj.reason || JSON.stringify(reasonObj)}
                </div>
              ))}
            </div>
          </div>
        )}

        <h2 className="text-lg font-normal">
          {t('patientInfo', { defaultValue: 'Patient Information' })}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {renderField('patientId')}
          {renderField('patientName')}
          {renderField('projectName')}
          {renderField('trackId')}
        </div>
        <hr className="border-divider" />

        <h2 className="text-lg font-normal">
          {t('contactInfo', { defaultValue: 'Contact Information' })}
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {renderField('phoneNumber')}
          {renderField('email')}
        </div>
      </div>
    </EpisodeOfCareDetails>
  );
};

EnrolmentDetails.propTypes = {
  enrolment: PropTypes.shape({
    patientId: PropTypes.string,
    eventId: PropTypes.string,
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
    status: PropTypes.string,
    context: PropTypes.shape({
      patientName: PropTypes.string,
      projectId: PropTypes.string,
      projectLabel: PropTypes.string,
      participationId: PropTypes.string,
      trackId: PropTypes.string,
      trackLabel: PropTypes.string,
      phones: PropTypes.arrayOf(
        PropTypes.shape({
          number: PropTypes.string,
        }),
      ),
      emails: PropTypes.arrayOf(
        PropTypes.shape({
          address: PropTypes.string,
        }),
      ),
    }),
    reviewQueueMetadata: PropTypes.shape({
      severity: PropTypes.string,
      type: PropTypes.string,
      reasons: PropTypes.arrayOf(
        PropTypes.shape({
          reason: PropTypes.string,
        }),
      ),
    }),
  }),
  visible: PropTypes.bool,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  onDelete: PropTypes.func,
  isEditable: PropTypes.bool,
};

EnrolmentDetails.defaultProps = {
  enrolment: {},
  visible: false,
  onCancel: () => {},
  onSave: null,
  onDelete: null,
  isEditable: false,
};

export default EnrolmentDetails;
